/**
 * The work record's photographs, listed and signed, and which one is open.
 * Composed by `useWorkController.ts`, and its own hook because nothing else on
 * the record waits for a picture — a storage hiccup here is its own failed
 * state and never the screen's `loadError`.
 */

import { useEffect, useState } from "react";

import { fetchWorkImages, signWorkImages } from "../lib/workImages.ts";
import type { PhotographLoad } from "./work.model.ts";

/**
 * The work's photographs and which one is open, composed by `useWorkController`.
 *
 * Its own hook because nothing else on the record depends on it: the chain,
 * the agents and the modals neither wait for a picture nor change one.
 */
export function useWorkPhotographs(workId: string): {
  photographs: PhotographLoad;
  selectedImageId: string | null;
  selectImage: (imageId: string) => void;
} {
  const [photographs, setPhotographs] = useState<PhotographLoad>({ kind: "loading" });
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);

  /**
   * The photographs, listed and then signed in one go.
   *
   * Failure here sets its own state rather than `loadError`: a work whose
   * pictures will not load still has a chain of title worth reading, and taking
   * the whole screen down over a storage hiccup would hide it.
   */
  useEffect(() => {
    let active = true;

    fetchWorkImages(workId)
      .then(async (found) => {
        const urls = await signWorkImages(found.map((photograph) => photograph.objectPath));
        if (active) setPhotographs({ kind: "loaded", photographs: found, urls });
      })
      .catch(() => {
        if (active) setPhotographs({ kind: "failed" });
      });

    return () => {
      active = false;
    };
  }, [workId]);

  return { photographs, selectedImageId, selectImage: setSelectedImageId };
}
